import { Ruler } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import type {
  Nalez,
  Vykres,
  VykresDetail,
  VykresVerze,
  VykresVerzeUpload,
  Zakazka,
} from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { VykresViewer } from '../components/VykresViewer.js';
import {
  Badge,
  Btn,
  Chyba,
  EmptyState,
  Input,
  Karta,
  Nacitani,
  Pole,
} from '../components/ui.js';
import { formatDen } from '../lib/format.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';

/**
 * Kancelář — Výkresy (Blok C): tabulka výkresů zakázky s historií verzí,
 * upload nového výkresu i nové VERZE (drag&drop), ruční „Označit jako
 * platnou" (audit hlásí backend) a sdílený viewer.
 *
 * HOOK z wedge: odpověď na upload verze nese `nabidnoutZmenu: true` →
 * výrazný dialog „Nová revize výkresu — založit nález pro přípraváře?"
 * (nejčastější spouštěč vícepráce — nesmí se odkliknout bez rozmyslu).
 */
export function VykresyKancelarPage() {
  const { maPravo } = useAuth();
  const queryClient = useQueryClient();
  const smiPsat = maPravo('vykresy.write');
  const [otevrenyId, setOtevrenyId] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  /** Verze čerstvě nahraná s nabidnoutZmenu — drží otevřený dialog hooku. */
  const [novaRevize, setNovaRevize] = useState<{
    vykres: Vykres;
    verze: VykresVerzeUpload;
  } | null>(null);

  const { data: zakazky, isLoading } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  // Zakázka z globálního přepínače v levém panelu (jeden kontext, ne další select).
  const { vybrana } = useVybranaZakazka(zakazky);

  const { data: vykresy, error } = useQuery({
    queryKey: ['vykresy', vybrana?.id],
    queryFn: () =>
      api.get<Vykres[]>(`/api/drawings?zakazkaId=${encodeURIComponent(vybrana?.id ?? '')}`),
    enabled: !!vybrana,
  });

  const obnov = () => {
    void queryClient.invalidateQueries({ queryKey: ['vykresy'] });
    void queryClient.invalidateQueries({ queryKey: ['vykres'] });
  };

  if (isLoading) return <Nacitani />;
  if (!zakazky?.length) return <p className="text-text-secondary">Zatím žádné zakázky.</p>;

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold">Výkresy</h1>
        {vybrana ? (
          <p className="text-base text-text-secondary">
            {vybrana.cislo} — {vybrana.nazev}
          </p>
        ) : null}
      </div>

      {chyba ? <Chyba>{chyba}</Chyba> : null}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}

      {vykresy && vykresy.length === 0 && (
        <EmptyState
          ikona={<Ruler size={22} strokeWidth={1.75} />}
          titulek="Zakázka zatím nemá výkresy"
          popis="Nahrajte první výkres níže — každý další soubor ke stejnému výkresu nahrávejte jako novou verzi, ať historie drží pohromadě."
        />
      )}

      {/* Galerie výkresů (karty) — viewer vybraného výkresu se otevírá pod
          galerií přes celou šířku (v kartě by byl stísněný). */}
      {vykresy && vykresy.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {vykresy.map((v) => (
            <VykresKarta
              key={v.id}
              vykres={v}
              otevreny={otevrenyId === v.id}
              smiPsat={smiPsat}
              naOtevreni={() => setOtevrenyId(otevrenyId === v.id ? null : v.id)}
              naChybu={setChyba}
              naNovouRevizi={(verze) => {
                setNovaRevize({ vykres: v, verze });
                obnov();
              }}
            />
          ))}
        </div>
      )}

      {otevrenyId && (
        <VykresDetailPanel vykresId={otevrenyId} smiPsat={smiPsat} naChybu={setChyba} />
      )}

      {smiPsat && vybrana && (
        <NovyVykresKarta zakazkaId={vybrana.id} naChybu={setChyba} naUlozeni={obnov} />
      )}

      {novaRevize && (
        <RevizeNalezDialog
          vykres={novaRevize.vykres}
          verze={novaRevize.verze}
          zavri={() => setNovaRevize(null)}
        />
      )}
    </>
  );
}

/** Karta výkresu v galerii: identita + platná verze + akce. */
function VykresKarta({
  vykres,
  otevreny,
  smiPsat,
  naOtevreni,
  naChybu,
  naNovouRevizi,
}: {
  vykres: Vykres;
  otevreny: boolean;
  smiPsat: boolean;
  naOtevreni: () => void;
  naChybu: (ch: string | null) => void;
  naNovouRevizi: (verze: VykresVerzeUpload) => void;
}) {
  const queryClient = useQueryClient();

  const nahrajVerzi = useMutation({
    mutationFn: (soubor: File) => {
      const form = new FormData();
      form.set('soubor', soubor, soubor.name);
      form.set('nazevSouboru', soubor.name);
      return api.upload<VykresVerzeUpload>(`/api/drawings/${vykres.id}/versions`, form);
    },
    onSuccess: (verze) => {
      naChybu(null);
      // HOOK revize → nález: server nabídl založení Změny.
      if (verze.nabidnoutZmenu) naNovouRevizi(verze);
      void queryClient.invalidateQueries({ queryKey: ['vykres', vykres.id] });
      void queryClient.invalidateQueries({ queryKey: ['vykresy'] });
    },
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  return (
    <article
      className={`flex flex-col gap-3 rounded-xl border bg-surface p-4 shadow-sm transition-colors duration-100 ${
        otevreny ? 'border-primary' : 'border-border hover:border-border-strong'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-tint text-primary"
        >
          <Ruler size={18} strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-text-heading">{vykres.cislo}</h3>
          <p className="truncate text-sm text-text-secondary">{vykres.nazev}</p>
        </div>
        {vykres.format !== 'PDF' && <Badge stav="neutral">{vykres.format}</Badge>}
      </div>
      <div>
        {vykres.platnaVerze ? (
          <Badge stav="success">
            v{vykres.platnaVerze.verze} ·{' '}
            {formatDen(vykres.platnaVerze.datum ?? vykres.platnaVerze.createdAt)}
          </Badge>
        ) : (
          <Badge stav="neutral">bez verze</Badge>
        )}
      </div>
      <div className="mt-auto flex flex-wrap gap-2">
        <Btn size="desktop" varianta={otevreny ? 'secondary' : 'primary'} onClick={naOtevreni}>
          {otevreny ? 'Zavřít' : 'Otevřít'}
        </Btn>
        {smiPsat && (
          <SouborVstup
            popisek={nahrajVerzi.isPending ? 'Nahrávám…' : 'Nahrát novou verzi'}
            ariaLabel={`Nahrát novou verzi výkresu ${vykres.cislo}`}
            disabled={nahrajVerzi.isPending}
            naSoubor={(soubor) => nahrajVerzi.mutate(soubor)}
          />
        )}
      </div>
    </article>
  );
}

/** Otevřený výkres: viewer přes celou šířku + sbalená historie verzí. */
function VykresDetailPanel({
  vykresId,
  smiPsat,
  naChybu,
}: {
  vykresId: string;
  smiPsat: boolean;
  naChybu: (ch: string | null) => void;
}) {
  const queryClient = useQueryClient();

  const { data: detail } = useQuery({
    queryKey: ['vykres', vykresId],
    queryFn: () => api.get<VykresDetail>(`/api/drawings/${vykresId}`),
  });

  const oznacPlatnou = useMutation({
    mutationFn: (verzeId: string) =>
      api.post<VykresDetail>(`/api/drawings/${vykresId}/versions/${verzeId}/oznacit-platnou`, {}),
    onSuccess: () => {
      naChybu(null);
      void queryClient.invalidateQueries({ queryKey: ['vykres', vykresId] });
      void queryClient.invalidateQueries({ queryKey: ['vykresy'] });
      void queryClient.invalidateQueries({ queryKey: ['vykres-pins'] });
    },
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  if (!detail) return <Nacitani />;

  return (
    <section className="flex flex-col gap-4 rounded-xl border border-primary bg-surface p-4 shadow-sm">
      <h2 className="text-base font-semibold text-text-heading">
        {detail.cislo} — {detail.nazev}
      </h2>
      <VykresViewer vykres={detail} />
      <details className="text-sm">
        <summary className="inline-flex min-h-11 cursor-pointer items-center font-semibold text-text-secondary">
          Historie verzí ({detail.verze.length})
        </summary>
        <ul className="mt-2 flex flex-col">
          {detail.verze.map((verze) => (
            <VerzeRadek
              key={verze.id}
              vykres={detail}
              verze={verze}
              smiPsat={smiPsat}
              oznacujiPlatnou={oznacPlatnou.isPending}
              naPlatnou={() => oznacPlatnou.mutate(verze.id)}
            />
          ))}
        </ul>
      </details>
    </section>
  );
}

/** Řádek verze v historii — stáhnout vždy (i starou), platnou označit ručně. */
function VerzeRadek({
  vykres,
  verze,
  smiPsat,
  oznacujiPlatnou,
  naPlatnou,
}: {
  vykres: VykresDetail;
  verze: VykresVerze;
  smiPsat: boolean;
  oznacujiPlatnou: boolean;
  naPlatnou: () => void;
}) {
  const [potvrzuji, setPotvrzuji] = useState(false);
  return (
    <li className="flex min-h-11 flex-wrap items-center gap-3 border-t border-border py-2 first:border-t-0">
      <span className="w-14 font-semibold">v{verze.verze}</span>
      <span className="w-28 text-text-secondary">{formatDen(verze.datum ?? verze.createdAt)}</span>
      <span className="min-w-0 flex-1 truncate text-text-secondary">{verze.nazevSouboru}</span>
      {verze.jePlatna ? (
        <Badge stav="success">platná</Badge>
      ) : (
        <Badge stav="neutral">historická</Badge>
      )}
      <a
        className="inline-flex min-h-11 items-center rounded-lg border border-border-strong bg-white px-4 text-base font-medium hover:bg-bg"
        href={`/api/drawings/${vykres.id}/versions/${verze.id}/soubor`}
        download={verze.nazevSouboru ?? undefined}
      >
        Stáhnout
      </a>
      {smiPsat && !verze.jePlatna && !potvrzuji && (
        <Btn varianta="secondary" onClick={() => setPotvrzuji(true)}>
          Označit jako platnou
        </Btn>
      )}
      {potvrzuji && (
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-error-text">
            Opravdu? Podle platné verze se staví — změna se zapíše do auditu.
          </span>
          <Btn
            varianta="danger"
            disabled={oznacujiPlatnou}
            onClick={() => {
              naPlatnou();
              setPotvrzuji(false);
            }}
          >
            Ano, označit v{verze.verze} jako platnou
          </Btn>
          <Btn varianta="secondary" onClick={() => setPotvrzuji(false)}>
            Zrušit
          </Btn>
        </span>
      )}
    </li>
  );
}

/** Založení nového výkresu: číslo + název + soubor (drag&drop / výběr). */
function NovyVykresKarta({
  zakazkaId,
  naChybu,
  naUlozeni,
}: {
  zakazkaId: string;
  naChybu: (ch: string | null) => void;
  naUlozeni: () => void;
}) {
  const [cislo, setCislo] = useState('');
  const [nazev, setNazev] = useState('');
  const [soubor, setSoubor] = useState<File | null>(null);

  const zaloz = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.set('zakazkaId', zakazkaId);
      form.set('cislo', cislo);
      form.set('nazev', nazev);
      if (soubor) form.set('soubor', soubor, soubor.name);
      if (soubor) form.set('nazevSouboru', soubor.name);
      return api.upload<Vykres>('/api/drawings', form);
    },
    onSuccess: () => {
      naChybu(null);
      setCislo('');
      setNazev('');
      setSoubor(null);
      naUlozeni();
    },
    onError: (err) => naChybu(hlaskaZChyby(err)),
  });

  return (
    <Karta nadpis="Nový výkres">
      <div className="grid gap-4 sm:grid-cols-2">
        <Pole popisek="Číslo výkresu" povinne>
          <Input placeholder="D.1.2" value={cislo} onChange={(e) => setCislo(e.target.value)} />
        </Pole>
        <Pole popisek="Název" povinne>
          <Input
            placeholder="Půdorys 2.NP"
            value={nazev}
            onChange={(e) => setNazev(e.target.value)}
          />
        </Pole>
      </div>
      <div className="mt-4">
        <DropZona soubor={soubor} naSoubor={setSoubor} />
      </div>
      <div className="mt-4">
        <Btn
          disabled={!cislo.trim() || !nazev.trim() || !soubor || zaloz.isPending}
          onClick={() => zaloz.mutate()}
        >
          {zaloz.isPending ? 'Nahrávám…' : 'Založit výkres (verze 1)'}
        </Btn>
      </div>
    </Karta>
  );
}

/** Drag&drop zóna (PDF/DWG) s fallbackem na výběr souboru. */
function DropZona({
  soubor,
  naSoubor,
}: {
  soubor: File | null;
  naSoubor: (soubor: File | null) => void;
}) {
  const [tahne, setTahne] = useState(false);
  const naDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setTahne(false);
    const prvni = e.dataTransfer.files?.[0];
    if (prvni) naSoubor(prvni);
  };
  return (
    <label
      className={`flex min-h-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-4 text-center ${
        tahne ? 'border-primary bg-primary-tint' : 'border-border-strong bg-white hover:bg-bg'
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setTahne(true);
      }}
      onDragLeave={() => setTahne(false)}
      onDrop={naDrop}
    >
      <span className="text-base font-semibold">
        {soubor ? `📄 ${soubor.name}` : 'Přetáhněte sem PDF nebo DWG, nebo klikněte pro výběr'}
      </span>
      <span className="text-sm text-text-secondary">
        PDF se zobrazí ve vieweru, DWG jen ke stažení.
      </span>
      <input
        type="file"
        accept=".pdf,.dwg,application/pdf"
        className="sr-only"
        aria-label="Soubor výkresu"
        onChange={(e) => naSoubor(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}

/** Skryté <input type=file> za tlačítkem — upload nové verze z řádku tabulky. */
function SouborVstup({
  popisek,
  ariaLabel,
  disabled,
  naSoubor,
}: {
  popisek: string;
  ariaLabel: string;
  disabled?: boolean;
  naSoubor: (soubor: File) => void;
}) {
  const vstupRef = useRef<HTMLInputElement>(null);
  return (
    <label className="inline-flex min-h-10 cursor-pointer items-center rounded-lg border border-border bg-surface px-3.5 text-sm font-semibold shadow-sm transition-colors duration-100 hover:bg-bg">
      {popisek}
      <input
        ref={vstupRef}
        type="file"
        accept=".pdf,.dwg,application/pdf"
        className="sr-only"
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(e) => {
          const soubor = e.target.files?.[0];
          if (soubor) naSoubor(soubor);
          e.target.value = '';
        }}
      />
    </label>
  );
}

/**
 * Dialog hooku „nová revize → nález": výrazný, ale jednoduchý — nejčastější
 * spouštěč vícepráce se nesmí odkliknout bez rozmyslu. [Založit nález] →
 * POST zalozit-nalez (zdroj revize_pd) s potvrzením; [Teď ne] zavře.
 */
function RevizeNalezDialog({
  vykres,
  verze,
  zavri,
}: {
  vykres: Vykres;
  verze: VykresVerzeUpload;
  zavri: () => void;
}) {
  const zaloz = useMutation({
    mutationFn: () =>
      api.post<Nalez>(`/api/drawings/${vykres.id}/versions/${verze.id}/zalozit-nalez`, {}),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Nová revize výkresu — založit nález pro přípraváře?"
        className="w-full max-w-lg rounded-2xl border-4 border-warning-border bg-white p-6 shadow-xl"
      >
        {zaloz.isSuccess ? (
          <>
            <h2 className="mb-2 text-xl font-bold">✔ Nález založen</h2>
            <p className="mb-5 text-base text-text-primary">
              Revize výkresu {vykres.cislo} (v{verze.verze}) je ve frontě nálezů — přípravář ji
              kvalifikuje na vícepráci/méněpráci.
            </p>
            <Btn onClick={zavri}>Zavřít</Btn>
          </>
        ) : (
          <>
            <h2 className="mb-2 text-xl font-bold">
              ⚠ Nová revize výkresu — založit nález pro přípraváře?
            </h2>
            <p className="mb-2 text-base text-text-primary">
              Nahráli jste verzi v{verze.verze} výkresu {vykres.cislo} „{vykres.nazev}". Nová revize
              projektové dokumentace je <strong>nejčastější spouštěč vícepráce</strong>.
            </p>
            <p className="mb-5 text-base text-text-primary">
              Nález padne do fronty přípraváře s důkazní vazbou na tuto revizi — nic se neztratí a
              nic se zatím nefakturuje.
            </p>
            {zaloz.error ? (
              <div className="mb-4">
                <Chyba>{hlaskaZChyby(zaloz.error)}</Chyba>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <Btn disabled={zaloz.isPending} onClick={() => zaloz.mutate()}>
                {zaloz.isPending ? 'Zakládám…' : 'Založit nález'}
              </Btn>
              <Btn varianta="secondary" disabled={zaloz.isPending} onClick={zavri}>
                Teď ne
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
